import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef, Output, EventEmitter, HostListener, Input, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileService, FilesResponse, FileDetails, YouTubeDetails } from '../../services/file.service';
import { LoginGmailComponent } from '../login-gmail/login-gmail.component';
import { AuthService } from '../../services/auth.service';
import { AnalyticsService } from '../../services/analytics.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { FileOperationEventsService, FileOperationEvent } from '../../services/file-operation-events.service';

// Angular Material Imports
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

// Constants
const MAX_FILE_SIZE_MB = 10;

// User type definitions
export type UserType = 'internal' | 'pilot' | 'general' | 'none';

// Interface for user roles configuration
interface UsersConfig {
  internalUsers: string[];
  pilotUsers: string[];
}

// Interface for the file to view in details area
export interface FileToView {
  fileType: string;
  content: string | ArrayBuffer | SafeResourceUrl | null;
  fileName: string;
  mimeType: string;
  needsExternalViewer?: boolean; // Optional flag for files that can't be previewed in-browser
  originalUrl?: string; // Optional original URL for fallback
  fallbackUrl?: SafeResourceUrl; // Optional fallback URL for fallback
  contentType?: 'blob' | 'base64' | 'text' | 'html' | 'youtube'; // Type of content for special handling
  isLoading?: boolean;
  error?: string;
  originalContent?: string; // Optional original content for showing source of HTML files
  youtubeData?: YouTubeDetails; // YouTube-specific data for video player
}

// Interface for file operation event
interface FileOperation {
  operation: 'download' | 'replace' | 'delete';
  fileType: string;
}

// Supported file types
const SUPPORTED_FILE_TYPES = ['.txt', '.docx', '.pdf', '.xlsx', '.png', '.html', '.png_thumbnail', '.gif'];

@Component({
  selector: 'app-file-processing',
  standalone: true,
  imports: [
    CommonModule, 
    LoginGmailComponent,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCheckboxModule
  ],
  templateUrl: './file-processing.component.html',
  styleUrls: ['./file-processing.component.css', './tooltip-styles.css']
})
export class FileProcessingComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  loading = false; // Global loading flag (used for initial loading)
  loadingFiles: { [key: string]: boolean } = {}; // File-specific loading flags for downloads
  loadingViewFiles: { [key: string]: boolean } = {}; // File-specific loading flags for viewing
  error: string | null = null;
  isUserLoggedIn = false;
  showLoginPrompt = false;
  userType: UserType = 'none'; // Default to none
  private destroy$ = new Subject<void>();
  bundleId: number = 1; // Default bundle ID
  selectedFileType: string | null = null; // Track the selected file type

  // Updated to support multiple bundles
  processingFiles: FilesResponse[] = []; // Array of file bundles
  selectedBundleIndex: number = 0; // Track the selected bundle

  // Properties for the file viewer in details pane
  viewerVisible = false;
  viewingFile: {
    fileType: string,
    content: string | ArrayBuffer | SafeResourceUrl | null,
    fileName: string
  } | null = null;
  viewerLoading = false;
  viewerError: string | null = null;

  // User configuration from users.json
  private usersConfig: UsersConfig = { internalUsers: [], pilotUsers: [] };

  // Properties for the processing files API
  fileTypes: string[] = ['txt', 'docx', 'pdf', 'xlsx', 'png', 'html', 'png_thumbnail', 'gif', 'youtube'];
  // File types for template *ngFor iteration (excluding youtube which has special handling)
  regularFileTypes: string[] = ['txt', 'docx', 'pdf', 'xlsx', 'png', 'html', 'png_thumbnail', 'gif'];
  uploadedFiles: { [key: string]: File | null } = {};
  @ViewChild('fileInput') fileInput!: ElementRef;
  uploading: { [key: string]: boolean } = {};
  uploadSuccess: { [key: string]: boolean } = {};
  uploadError: { [key: string]: string | null } = {};

  // Emit event to the parent component to show file in details pane
  @Output() viewFileInDetails = new EventEmitter<FileToView>();

  // Input properties to receive subject and section from single-tree component
  @Input() selectedSubject: string | null = null;
  @Input() selectedSection: string | null = null;

  // Add this as a class property in the FileProcessingComponent
  @Input() adminMode: boolean = false;

  // Input properties for height and preview settings
  @Input() selectedHeight: string = 'large'; // Height selector value from main-area
  @Input() selectedPreview: string = 'none'; // Preview selector value from main-area

  // Property to track the checked bundle ID
  checkedBundleId: string | number | null = null;

  // Properties for in-cell preview functionality
  cellPreviews: { [key: string]: { content: string | SafeResourceUrl | null, isLoading: boolean, error: string | null } } = {};

  // Properties for thumbnail functionality
  cellThumbnails: { [key: string]: { url: string | null, isLoading: boolean, error: string | null } } = {};

  // Material table columns definition with checkbox column
  displayedColumns: string[] = [
    'checkbox',
    'id', 
    'subject', 
    'section', 
    'topic', 
    'subtopic', 
    'txt', 
    'docx', 
    'pdf', 
    'xlsx', 
    'png', 
    'html', 
    'png_thumbnail', 
    'gif',
    'youtube'
  ];

  // Properties for resizable columns
  private isResizing = false;
  private currentResizeColumn: string | null = null;
  private startX = 0;
  private startWidth = 0;
  private tableElement: HTMLElement | null = null;
  private resizeIndicator: HTMLElement | null = null;
  private columnWidths: {[key: string]: number} = {
    subject: 150,
    section: 150,
    topic: 150,
    subtopic: 150
  };

  constructor(
    private fileService: FileService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private fileOpEvents: FileOperationEventsService,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit(): void {
    console.log('FileProcessingComponent - Init');
    this.loading = true;
    
    // Load users configuration first
    this.loadUsersConfig().then(() => {
      console.log('Users config loaded');
      
      // Check initial login status after config is loaded
      this.checkUserLoginStatus();
      
      // Subscribe to auth state changes
      this.authService.authState$.pipe(
        takeUntil(this.destroy$)
      ).subscribe((isLoggedIn: boolean) => {
        console.log('🔄 Auth state change detected:', isLoggedIn);
        this.isUserLoggedIn = isLoggedIn;
        if (isLoggedIn) {
          const userEmail = this.getUserEmail();
          this.onUserLoggedIn(userEmail);
        } else {
          this.userType = 'none';
        }
        this.checkUserLoginStatus();
        this.cdr.detectChanges(); // Force UI update
      });
    });
    
    // Custom file operation events - This is a custom implementation
    document.addEventListener('fileOperation', ((event: CustomEvent<FileOperation>) => {
      console.log('File operation event received:', event.detail);
      
      if (event.detail.operation === 'download') {
        this.downloadProcessingFileFromBundle(event.detail.fileType, this.selectedBundleIndex);
      } else if (event.detail.operation === 'replace') {
        this.replaceFileInSpecificBundle(event.detail.fileType, this.selectedBundleIndex);
      } else if (event.detail.operation === 'delete') {
        this.deleteProcessingFileFromBundle(event.detail.fileType, this.selectedBundleIndex);
      }
    }) as EventListener);

    // Subscribe to the standard fileOperation events (refresh, deleted)
    this.fileOpEvents.fileOperation$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((event: FileOperationEvent) => {
      console.log('File operation standard event received:', event);
      if (event === 'refresh' || event === 'deleted') {
        // Refresh the files when needed
        this.loadFileBundle();
      }
    });

    // Try loading all bundles first
    this.loadFileBundle();

    // Initialize resize event listeners
    this.initResizeListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Changes detected in file-processing component:', changes);
    
    // Check if selectedSubject or selectedSection have changed
    if ((changes['selectedSubject'] && !changes['selectedSubject'].firstChange) || 
        (changes['selectedSection'] && !changes['selectedSection'].firstChange)) {
      
      console.log('Subject or section changed, reloading bundles...');
      console.log('Current values - Subject:', this.selectedSubject, 'Section:', this.selectedSection);
      
      // Only reload if we have both subject and section
      if (this.selectedSubject && this.selectedSection) {
        this.loadFileBundle();
      }
    }

    // Check if height or preview settings have changed
    if ((changes['selectedHeight'] && !changes['selectedHeight'].firstChange) || 
        (changes['selectedPreview'] && !changes['selectedPreview'].firstChange)) {
      
      console.log('Height or preview settings changed:', {
        height: this.selectedHeight,
        preview: this.selectedPreview,
        shouldShowPreview: this.shouldShowInCellPreview(),
        shouldShowThumbnails: this.shouldShowThumbnails()
      });

      // Clear existing previews when settings change
      this.cellPreviews = {};
      
      // Load new previews if conditions are met
      setTimeout(() => {
        this.loadAutoPreviewsIfNeeded();
        this.loadThumbnailsForVisibleBundles(); // Also load thumbnails when switching to thumbnail mode
        this.cdr.detectChanges();
      }, 50);
    }
  }

  // Load users configuration from assets/users.json
  private async loadUsersConfig(): Promise<void> {
    try {
      const data = await firstValueFrom(this.http.get<UsersConfig>('assets/users.json'));
      this.usersConfig = data;
      console.log('Users config loaded:', this.usersConfig);
    } catch (error) {
      console.error('Error loading users config:', error);
      // Set default values if loading fails
      this.usersConfig = {
        internalUsers: ['admin@intellipreptech.com'],
        pilotUsers: []
      };
    }
  }

  // Helper method to get user email from stored user data
  getUserEmail(): string | null {
    const storedUser = localStorage.getItem('googleUser');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        return userData.email || null;
      } catch (e) {
        console.error('Error parsing user data:', e);
        return null;
      }
    }
    return null;
  }

  // Method to check if user has permission for specific actions
  canPerformAction(action: 'download' | 'upload' | 'replace' | 'delete'): boolean {
    if (!this.isUserLoggedIn) return false;

    switch (action) {
      case 'download':
        // All logged-in users can download
        return true;
      case 'upload':
      case 'replace':
      case 'delete':
        // Only internal users can perform these actions
        return this.userType === 'internal';
      default:
        return false;
    }
  }

  // Check if the current user is an internal user
  private isInternalUser(email: string | null): boolean {
    if (!email) return false;

    // Convert email to lowercase for case-insensitive comparison
    const lowerEmail = email.toLowerCase();

    // Check if the email is in the list of internal users from config
    if (this.usersConfig.internalUsers.map(e => e.toLowerCase()).includes(lowerEmail)) {
      return true;
    }

    // Check if email domain matches any internal domain
    const internalDomains = ['@internal.com', '@intellipreptech.com'];
    return internalDomains.some(domain => lowerEmail.includes(domain));
  }

  // Check if the current user is a pilot user
  private isPilotUser(email: string | null): boolean {
    if (!email) return false;

    // Convert email to lowercase for case-insensitive comparison
    const lowerEmail = email.toLowerCase();

    // Check if the email is in the list of pilot users from config
    if (this.usersConfig.pilotUsers.map(e => e.toLowerCase()).includes(lowerEmail)) {
      return true;
    }

    // Check if email domain matches any pilot domain
    return lowerEmail.includes('@pilot.com');
  }

  checkUserLoginStatus(): void {
    const storedUser = localStorage.getItem('googleUser');
    const wasLoggedIn = this.isUserLoggedIn;
    this.isUserLoggedIn = !!storedUser;

    // Also check auth service state
    const authServiceUser = this.authService.getUserInfo();
    if (authServiceUser && !this.isUserLoggedIn) {
      this.isUserLoggedIn = true;
    }

    if (this.isUserLoggedIn) {
      // Determine user type based on stored data or role
      // In a real application, you would get this from your auth service
      // This is a placeholder implementation
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          this.userType = userData.userType || 'general';
        } catch (e) {
          console.error('Error parsing user data:', e);
          this.userType = 'general'; // Default to general if parsing fails
        }
      } else {
        // For demo purposes, you can set a default user type or
        // implement your own logic to determine the user type

        // Check email to determine user type
        const userEmail = this.getUserEmail() || authServiceUser?.email;

        if (this.isInternalUser(userEmail)) {
          this.userType = 'internal';
        } else if (this.isPilotUser(userEmail)) {
          this.userType = 'pilot';
        } else {
          this.userType = 'general';
        }
      }
    } else {
      this.userType = 'none';
    }

    console.log('🔍 DEBUG - User login status checked in File Processing:', {
      isLoggedIn: this.isUserLoggedIn,
      userType: this.userType,
      email: this.getUserEmail(),
      authServiceUser: authServiceUser ? 'exists' : 'null',
      storedUser: storedUser ? 'exists' : 'null',
      internalUsers: this.usersConfig.internalUsers
    });

    // If user just logged in, reload file bundle
    if (!wasLoggedIn && this.isUserLoggedIn) {
      this.loadFileBundle();
    }
  }

  // Force refresh auth state - useful for debugging
  forceRefreshAuthState(): void {
    console.log('🔄 Force refreshing auth state...');
    this.checkUserLoginStatus();
    this.cdr.detectChanges();
  }

  // Helper to get button color based on user type
  getDownloadButtonClass(): string {
    switch (this.userType) {
      case 'internal':
        return 'download-button-internal';
      case 'pilot':
        return 'download-button-pilot';
      case 'general':
        return 'download-button-general';
      default:
        return 'download-button';
    }
  }

  // Updated to use the new API endpoint to load all bundles
  loadFileBundle(): void {
    console.log('Loading file bundles');
    this.loading = true;
    this.error = null;
    
    // If both subject and section are selected, use the filter API
    if (this.selectedSubject && this.selectedSection) {
      this.loadFilteredBundles(this.selectedSubject, this.selectedSection);
    } else {
      // Otherwise load all bundles
      this.loadAllBundles();
    }
  }

  // Method to load all bundles
  private loadAllBundles(): void {
    this.fileService.getAllFileBundles().subscribe({
      next: (response) => {
        console.log('All bundles API Response:', response);

        this.processingFiles = response;

        // If we got a valid response with bundles
        if (this.processingFiles && this.processingFiles.length > 0) {
          this.handleSuccessfulBundleLoad();
        } else {
          console.warn('No bundles found or empty array returned, falling back to single bundle');
          // If no bundles found or empty array, try loading a single bundle
          this.fallbackToSingleBundle();
        }
      },
      error: (err) => {
        console.error('Error loading all bundles:', err);
        // Don't show the error yet, try the fallback first
        this.fallbackToSingleBundle();
      }
    });
  }

  // Method to load filtered bundles based on subject and section
  loadFilteredBundles(subject: string, section: string): void {
    this.loading = true;
    this.error = null;
    
    console.log(`Loading filtered bundles for subject: ${subject}, section: ${section}`);
    
    this.fileService.filterFileBundles(subject, section).subscribe({
      next: (response) => {
        console.log('Filtered bundles API Response:', response);
        
        this.processingFiles = response;
        
        // If we got a valid response with bundles
        if (this.processingFiles && this.processingFiles.length > 0) {
          this.handleSuccessfulBundleLoad();
        } else {
          console.warn('No filtered bundles found');
          this.handleFailedBundleLoad('No bundles found for the selected subject and section');
        }
      },
      error: (err) => {
        console.error('Error loading filtered bundles:', err);
        this.handleFailedBundleLoad('Failed to load filtered bundles. Please try again later.');
      }
    });
  }

  // Fallback method if getting all bundles fails
  private fallbackToSingleBundle(): void {
    console.log('Falling back to single bundle retrieval...');

    this.fileService.getFileBundle(this.bundleId).subscribe({
      next: (response) => {
        console.log('Single bundle API Response:', response);

        if (response) {
          // Convert single bundle to array format
          this.processingFiles = [response];
          this.selectedBundleIndex = 0;

          this.handleSuccessfulBundleLoad();
        } else {
          // If both attempts fail, show an error
          this.handleFailedBundleLoad('No valid file bundles found');
        }
      },
      error: (err) => {
        console.error('Error loading single bundle fallback:', err);
        this.handleFailedBundleLoad('Failed to load files. Please try again later.');
      }
    });
  }

  // Handle successful bundle loading
  private handleSuccessfulBundleLoad(): void {
    const selectedBundle = this.getSelectedBundle();

    if (selectedBundle && selectedBundle.files) {
      this.fileTypes = Object.keys(selectedBundle.files);
      console.log('File types found:', this.fileTypes);
    } else {
      // If files property doesn't exist, use default file types
      this.fileTypes = ['txt', 'docx', 'pdf', 'xlsx', 'png'];
      console.log('No files property in bundle, using default file types:', this.fileTypes);
    }

    this.loading = false;
    console.log('Bundles loaded, selected bundle index:', this.selectedBundleIndex);
    this.cdr.detectChanges(); // Force UI update

    // Load previews automatically after bundles are loaded
    setTimeout(() => {
      this.loadAutoPreviewsIfNeeded();
      this.loadThumbnailsForVisibleBundles(); // Also load thumbnails when bundles are loaded
    }, 200);
  }

  // Handle failed bundle loading
  private handleFailedBundleLoad(errorMessage: string): void {
    // Set default file types so UI can at least show placeholders
    this.fileTypes = ['txt', 'docx', 'pdf', 'xlsx', 'png'];
    this.error = errorMessage;
    this.loading = false;
    this.cdr.detectChanges(); // Force UI update
  }

  // Method to set the current selected bundle
  selectBundle(index: number): void {
    if (index >= 0 && index < this.processingFiles.length) {
      this.selectedBundleIndex = index;
      this.bundleId = this.processingFiles[index].id;
      this.selectedFileType = null; // Reset selected file type on bundle change
      this.cdr.detectChanges(); // Force UI update
    }
  }

  // Get the currently selected bundle
  getSelectedBundle(): FilesResponse | null {
    if (!this.processingFiles || !Array.isArray(this.processingFiles) || this.processingFiles.length === 0) {
      return null;
    }

    if (this.selectedBundleIndex >= this.processingFiles.length) {
      this.selectedBundleIndex = 0; // Reset to first bundle if index is out of bounds
    }

    return this.processingFiles[this.selectedBundleIndex];
  }

  // Updated to check if a file exists in the currently selected bundle
  fileExists(fileType: string): boolean {
    const selectedBundle = this.getSelectedBundle();

    if (!selectedBundle || !selectedBundle.files) {
      return false;
    }

    const file = selectedBundle.files[fileType];
    if (!file) {
      return false;
    }

    // Check if the file has a valid link
    return !!file.link && file.link.trim() !== '';
  }

  // Get a specific property from a file in a specific bundle
  getFilePropertyFromBundle(fileType: string, property: keyof FileDetails, bundleOrIndex?: FilesResponse | number): string {
    let bundle: FilesResponse | null = null;

    if (typeof bundleOrIndex === 'number') {
      // bundleIndex provided
      bundle = this.processingFiles[bundleOrIndex];
    } else if (bundleOrIndex && typeof bundleOrIndex === 'object') {
      // bundle object provided directly
      bundle = bundleOrIndex;
    } else {
      // Use selected bundle as fallback
      bundle = this.getSelectedBundle();
    }

    if (!bundle?.files?.[fileType]) {
      return '';
    }

    const fileDetails = bundle.files[fileType];
    const value = fileDetails?.[property];
    
    // Handle the thumbnail property specially since it's an object
    if (property === 'thumbnail') {
      // For thumbnail property, we don't want to return it as a string
      // This method should probably not be used for thumbnail data
      return '';
    }
    
    return (typeof value === 'string') ? value : '';
  }

  // Helper method to safely get the filename from the selected bundle
  getFileName(fileType: string): string {
    const selectedBundle = this.getSelectedBundle();

    if (!selectedBundle?.files?.[fileType]) {
      return '';
    }

    const fileDetails = selectedBundle.files[fileType];

    // Use the filename field directly from the API response
    if (fileDetails.filename) {
      return fileDetails.filename;
    }

    // Fallback to extracting from URL if filename is not available
    if (fileDetails.link) {
      try {
        const url = new URL(fileDetails.link);
        const pathSegments = url.pathname.split('/');
        return decodeURIComponent(pathSegments[pathSegments.length - 1]) || '';
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
    }

    // If all else fails, return a default name based on file type
    return this.getDefaultFilename(fileType);
  }

  // Helper method to generate a default filename based on file type
  getDefaultFilename(fileType: string): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    switch (fileType) {
      case 'pdf': return `document-${timestamp}.pdf`;
      case 'docx': return `document-${timestamp}.docx`;
      case 'xlsx': return `spreadsheet-${timestamp}.xlsx`;
      case 'png': return `image-${timestamp}.png`;
      case 'txt': return `text-${timestamp}.txt`;
      default: return `file-${timestamp}.${fileType}`;
    }
  }

  // Method to get file accept attribute for file inputs
  getFileAccept(fileType: string): string {
    switch (fileType) {
      case 'pdf': return '.pdf,application/pdf';
      case 'docx': return '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xlsx': return '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'png': return '.png,image/png';
      case 'png_thumbnail': return '.png,image/png';
      case 'txt': return '.txt,text/plain';
      case 'html': return '.html,text/html';
      case 'gif': return '.gif,image/gif';
      default: return '';
    }
  }

  // Method to validate file type and size
  validateFile(file: File, fileType: string): { valid: boolean, message: string | null } {
    // Check file size (10MB limit)
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_FILE_SIZE_MB) {
      return {
        valid: false,
        message: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Your file is ${fileSizeInMB.toFixed(2)}MB.`
      };
    }

    // Check file extension
    const fileName = file.name.toLowerCase();
    
    // Special handling for png_thumbnail - accept .png files
    if (fileType === 'png_thumbnail') {
      if (!fileName.endsWith('.png')) {
        return {
          valid: false,
          message: `Invalid file type for PNG_THUMBNAIL. Expected .png file.`
        };
      }
      return { valid: true, message: null };
    }

    // For regular file types, check extension matches
    const expectedExtension = `.${fileType}`;
    if (!fileName.endsWith(expectedExtension)) {
      return {
        valid: false,
        message: `Invalid file type. Expected ${fileType} file.`
      };
    }

    return { valid: true, message: null };
  }

  // Method to handle file selection from input
  onFileSelected(event: Event, fileType: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Validate file
    const validation = this.validateFile(file, fileType);
    if (!validation.valid) {
      this.uploadError[fileType] = validation.message;
      this.cdr.detectChanges();
      return;
    }

    if (this.fileExists(fileType)) {
      this.handleFileReplace(fileType, file);
    } else {
      this.handleFileUpload(fileType, file);
    }
  }

  // Method to trigger file input dialog for uploading
  triggerFileUpload(fileType: string): void {
    this.selectedFileType = fileType; // Set selected file

    if (!this.isUserLoggedIn) {
      this.showLoginPrompt = true;
      this.cdr.detectChanges(); // Force UI update
      return;
    }

    if (!this.canPerformAction('upload')) {
      this.error = 'You do not have permission to upload files.';
      return;
    }

    // If a file already exists, don't allow upload
    if (this.fileExists(fileType)) {
      this.error = `A ${fileType} file already exists. Use Replace instead.`;
      return;
    }

    // Create a temporary file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = this.getFileAccept(fileType);
    fileInput.click();

    fileInput.addEventListener('change', (event) => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const file = files[0];

        // Validate file
        const validation = this.validateFile(file, fileType);
        if (!validation.valid) {
          this.uploadError[fileType] = validation.message;
          this.cdr.detectChanges();
          return;
        }

        this.handleFileUpload(fileType, file);
      }
    });
  }

  // Updated to use the new bundle API
  handleFileUpload(fileType: string, file: File): void {
    this.uploading[fileType] = true;
    this.uploadError[fileType] = null;
    this.uploadSuccess[fileType] = false;

    const formData = new FormData();
    formData.append('file', file);

    this.fileService.uploadFileToBundle(this.bundleId, fileType, formData).subscribe({
      next: (response) => {
        this.uploading[fileType] = false;
        this.uploadSuccess[fileType] = true;
        // Reload the files after successful upload
        this.loadFileBundle();

        // Reset success indicator after 3 seconds
        setTimeout(() => {
          this.uploadSuccess[fileType] = false;
          this.cdr.detectChanges();
        }, 3000);

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.uploading[fileType] = false;
        this.uploadError[fileType] = 'Failed to upload file. Please try again.';
        console.error('Error uploading file:', err);
        this.cdr.detectChanges();
      }
    });
  }

  // Method to replace existing file - updated to use new bundle API
  replaceFile(fileType: string): void {
    this.selectedFileType = fileType; // Set selected file

    if (!this.isUserLoggedIn) {
      this.showLoginPrompt = true;
      this.cdr.detectChanges(); // Force UI update
      return;
    }

    if (!this.canPerformAction('replace')) {
      this.error = 'You do not have permission to replace files.';
      return;
    }

    // Check if the file exists
    if (!this.fileExists(fileType)) {
      this.error = `No ${fileType} file exists to replace. Use Upload instead.`;
      return;
    }

    // Create a temporary file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = this.getFileAccept(fileType);
    fileInput.click();

    fileInput.addEventListener('change', (event) => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const file = files[0];

        // Validate file
        const validation = this.validateFile(file, fileType);
        if (!validation.valid) {
          this.uploadError[fileType] = validation.message;
          this.cdr.detectChanges();
          return;
        }

        this.handleFileReplace(fileType, file);
      }
    });
  }

  // Updated to use the new bundle API
  handleFileReplace(fileType: string, file: File): void {
    this.uploading[fileType] = true;
    this.uploadError[fileType] = null;
    this.uploadSuccess[fileType] = false;

    const formData = new FormData();
    formData.append('file', file);

    this.fileService.replaceFileInBundle(this.bundleId, fileType, formData).subscribe({
      next: (response) => {
        this.uploading[fileType] = false;
        this.uploadSuccess[fileType] = true;
        // Reload the files after successful replacement
        this.loadFileBundle();
        this.fileOpEvents.emit('refresh'); // Emit refresh event
        // Reset success indicator after 3 seconds
        setTimeout(() => {
          this.uploadSuccess[fileType] = false;
          this.cdr.detectChanges();
        }, 3000);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.uploading[fileType] = false;
        this.uploadError[fileType] = 'Failed to replace file. Please try again.';
        console.error('Error replacing file:', err);
        this.cdr.detectChanges();
      }
    });
  }

  // Updated to use the new bundle API and add confirmation dialog
  deleteProcessingFileFromBundle(fileType: string, bundleIndex: number): void {
    // Update selected bundle and file type
    this.selectedBundleIndex = bundleIndex;
    this.selectedFileType = fileType;
    this.bundleId = this.processingFiles[bundleIndex].id;

    if (!this.isUserLoggedIn) {
      this.showLoginPrompt = true;
      this.cdr.detectChanges(); // Force UI update
      return;
    }

    if (!this.canPerformAction('delete')) {
      this.error = 'You do not have permission to delete files.';
      return;
    }

    // Check if the file exists
    if (!this.fileExistsInBundle(fileType, this.processingFiles[bundleIndex])) {
      this.error = `No ${fileType} file exists to delete.`;
      return;
    }

    // Ask for confirmation
    if (confirm(`Are you sure you want to delete the ${this.getFileTypeLabel(fileType)} file?`)) {
      // Use combined key for operation state
      this.uploading[fileType + '-' + bundleIndex] = true;

      // Track file delete start in GA4
      const fileName = this.getFileNameFromBundle(fileType, this.processingFiles[bundleIndex]);
      this.analyticsService.trackFileDelete({
        name: fileName,
        type: fileType,
        size: 0,
        bundle_id: this.bundleId,
        bundle_index: bundleIndex,
        user_type: this.userType
      });

      this.fileService.deleteFileFromBundle(this.bundleId, fileType).subscribe({
        next: (response) => {
          this.uploading[fileType + '-' + bundleIndex] = false;

          // Track successful delete in GA4
          this.analyticsService.trackFileDelete({
            name: fileName,
            type: fileType,
            size: 0,
            bundle_id: this.bundleId,
            bundle_index: bundleIndex,
            user_type: this.userType
          });

          // Manually update the local state to reflect the deletion immediately
          if (this.processingFiles && this.processingFiles.length > bundleIndex &&
              this.processingFiles[bundleIndex] && this.processingFiles[bundleIndex].files) {
            delete this.processingFiles[bundleIndex].files[fileType];

            // Set file to not loaded state
            if (this.processingFiles[bundleIndex].files[fileType]) {
              this.processingFiles[bundleIndex].files[fileType] = null as any;
            }
          }

          // Show success message with combined key
          this.uploadSuccess[fileType + '-' + bundleIndex] = true;
          setTimeout(() => {
            this.uploadSuccess[fileType + '-' + bundleIndex] = false;
            this.cdr.detectChanges();
          }, 3000);

          // Reload the files after successful deletion
          // But also allow UI to update immediately
          this.cdr.detectChanges();
          this.loadFileBundle();
          this.fileOpEvents.emit('deleted'); // Emit deleted event
        },
        error: (err) => {
          this.uploading[fileType + '-' + bundleIndex] = false;
          this.uploadError[fileType + '-' + bundleIndex] = 'Failed to delete file. Please try again.';
          
          // Track delete error in GA4
          this.analyticsService.trackFileDelete({
            name: fileName,
            type: fileType,
            size: 0,
            bundle_id: this.bundleId,
            bundle_index: bundleIndex,
            user_type: this.userType,
            error_message: err.message || 'Unknown error'
          });
          
          console.error('Error deleting file:', err);
          this.cdr.detectChanges();
        }
      });
    }
  }

  // Helper method to get file type label
  getFileTypeLabel(fileType: string): string {
    const labels: { [key: string]: string } = {
      'txt': 'TXT',
      'docx': 'DOCX',
      'pdf': 'PDF',
      'xlsx': 'XLSX',
      'png': 'PNG',
      'html': 'HTML',
      'png_thumbnail': 'PNG_THUMBNAIL',
      'gif': 'GIF'
    };
    return labels[fileType] || fileType.toUpperCase();
  }

  // Helper method to get accept type for file input
  getAcceptTypeForFileType(fileType: string): string {
    const acceptTypes: { [key: string]: string } = {
      'txt': '.txt,text/plain',
      'docx': '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'pdf': '.pdf,application/pdf',
      'xlsx': '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'png': '.png,image/png',
      'html': '.html,text/html',
      'png_thumbnail': '.png,image/png',
      'gif': '.gif,image/gif'
    };
    return acceptTypes[fileType] || '';
  }

  // Helper method to get file icon class
  getFileIconClass(fileType: string): string {
    switch (fileType) {
      case 'pdf': return 'fa-file-pdf';
      case 'docx': return 'fa-file-word';
      case 'xlsx': return 'fa-file-excel';
      case 'png': return 'fa-file-image';
      case 'txt': return 'fa-file-alt';
      default: return 'fa-file';
    }
  }

  // Method to format date
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  // Helper: icon for preview placeholder per file type
  getPreviewIcon(type: string): string {
    const icons: Record<string, string> = {
      txt: 'visibility', docx: 'description', pdf: 'picture_as_pdf',
      xlsx: 'table_chart', png: 'image', html: 'code',
      png_thumbnail: 'image', gif: 'gif'
    };
    return icons[type] || 'visibility';
  }

  // Helper: how to render preview content per file type
  getPreviewRenderType(type: string): string {
    if (type === 'txt') return 'text';
    if (type === 'docx' || type === 'xlsx') return 'innerHTML';
    if (type === 'pdf' || type === 'html') return 'iframe';
    return 'image';
  }

  // Helper: human-readable label for tooltips
  getFileTypeTooltipLabel(type: string): string {
    const labels: Record<string, string> = {
      gif: 'animated GIF',
      png_thumbnail: 'thumbnail'
    };
    return labels[type] || 'file';
  }

  onUserLoggedIn(userName: string | null): void {
    if (userName) {
      this.isUserLoggedIn = true;
      this.showLoginPrompt = false;
      this.checkUserLoginStatus(); // Update user type
      this.loadFileBundle(); // Load file bundle
      console.log('User logged in from File Processing component:', userName);
      this.cdr.detectChanges(); // Force UI update
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Remove resize event listeners
    this.removeResizeListeners();
  }

  ngAfterViewInit(): void {
    // Load previews automatically when view is initialized
    setTimeout(() => {
      this.loadAutoPreviewsIfNeeded();
    }, 100);
  }

  // Method to automatically load previews when conditions are met
  private loadAutoPreviewsIfNeeded(): void {
    if (!this.shouldShowInCellPreview()) {
      // Clear any existing previews when preview mode is disabled
      this.cellPreviews = {};
      return;
    }

    // Auto-load all previews if "Show Preview - All" is selected
    if (this.shouldAutoLoadAllPreviews() && this.processingFiles && this.processingFiles.length > 0) {
      // Load previews for files that exist in current bundles
      this.processingFiles.forEach((bundle, bundleIndex) => {
        this.fileTypes.forEach(fileType => {
          if (this.fileExistsInBundle(fileType, bundle)) {
            const previewKey = `${fileType}-${bundleIndex}`;
            // Only load if not already loaded or loading
            if (!this.cellPreviews[previewKey]) {
              this.loadInCellPreview(fileType, bundleIndex);
            }
          }
        });
      });
    }
  }

  private saveFile(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // View a file in the details pane
  viewFile(fileType: string): void {
    this.selectedFileType = fileType; // Set selected file

    if (!this.fileExists(fileType)) {
      this.error = `No ${this.getFileTypeLabel(fileType)} file exists to view.`;
      return;
    }

    this.loadingViewFiles[fileType] = true;
    this.error = null;
    const fileName = this.getFileName(fileType);

    // Emit an empty file object to indicate loading has started
    this.viewFileInDetails.emit({
      fileType,
      content: null,
      fileName,
      mimeType: this.getFileMimeType(fileType),
      isLoading: true
    });

    // Use the bundle-specific endpoint to get the file
    this.fileService.downloadFileByType(this.bundleId, fileType).subscribe({
      next: (blob) => {
        this.loadingViewFiles[fileType] = false;

        // Prepare file for viewing and emit to parent component
        this.prepareAndEmitFileForViewing(fileType, blob, fileName);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading file for viewing:', err);
        this.loadingViewFiles[fileType] = false;
        this.error = 'Failed to load file for viewing. Please try again.';

        // Emit an empty file object to indicate loading has failed
        this.viewFileInDetails.emit({
          fileType,
          content: null,
          fileName,
          mimeType: this.getFileMimeType(fileType),
          isLoading: false,
          error: 'Failed to load file for viewing. Please try again.'
        });

        this.cdr.detectChanges();
      }
    });
  }

  // Directly add a console logging function for text files
  private logTextFileContent(fileContent: string | null | undefined, fileName: string): void {
    console.warn('========= TEXT FILE CONTENT LOGGING ==========');
    console.warn(`File name: ${fileName}`);
    console.warn(`Content type: ${typeof fileContent}`);
    console.warn(`Content is null/undefined: ${fileContent == null}`);

    if (typeof fileContent === 'string') {
      console.warn(`Content length: ${fileContent.length}`);
      console.warn(`Content is empty string: ${fileContent === ''}`);
      console.warn(`Content is whitespace only: ${fileContent.trim() === ''}`);
      console.warn(`First 100 chars: "${fileContent.substring(0, 100).replace(/\n/g, '\\n').replace(/\r/g, '\\r')}"`);

      // Additionally log as table for better visibility
      console.table({
        'File Name': fileName,
        'Content Type': typeof fileContent,
        'Content Length': fileContent.length,
        'Is Empty': fileContent === '',
        'Is Whitespace': fileContent.trim() === '',
        'First 50 chars': fileContent.substring(0, 50)
      });
    } else {
      console.warn('Content is not a string, cannot show details');
    }
    console.warn('============================================');
  }

  // Prepare downloaded file for viewing and emit to parent
  private prepareAndEmitFileForViewing(fileType: string, blob: Blob, fileName: string): void {
    // Clear any previous errors
    this.viewerError = null;

    // Set content type for special file handling
    let contentType: 'blob' | 'base64' | 'text' | 'html' = 'blob';
    let needsExternalViewer = false;
    const fileToView: FileToView = {
      fileType: fileType,
      content: null,
      fileName: fileName,
      mimeType: this.getFileMimeType(fileType),
      isLoading: true
    };
    
    // Emit right away to show loading state
    this.viewFileInDetails.emit(fileToView);

    // Process different file types
    try {
      const reader = new FileReader();
      
      // Set up reader onload handler based on file type
      reader.onload = () => {
        const result = reader.result;
        
        switch (fileType) {
          case 'txt':
            fileToView.contentType = 'text';
            fileToView.content = result as string;
            this.logTextFileContent(result as string, fileName);
            break;
            
          case 'docx':
            // Use mammoth.js to convert docx to HTML
            if (result instanceof ArrayBuffer) {
              mammoth.convertToHtml({ arrayBuffer: result })
                .then((resultObject) => {
                  fileToView.contentType = 'html';
                  fileToView.content = resultObject.value;
                  fileToView.isLoading = false;
                  this.viewFileInDetails.emit(fileToView);
                })
                .catch((error) => {
                  console.error('Error converting DOCX:', error);
                  fileToView.error = `Error loading DOCX: ${error.message || 'Unknown error'}`;
                  fileToView.isLoading = false;
                  this.viewFileInDetails.emit(fileToView);
                });
              return; // Return early as we'll emit later
            } else {
              fileToView.error = 'Failed to load DOCX file: Invalid content';
            }
            break;
            
          case 'pdf':
            // For PDF, use a safe URL to display in iframe
            fileToView.contentType = 'blob';
            fileToView.content = this.sanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(blob)
            );
            break;
            
          case 'xlsx':
            // Use SheetJS to parse Excel file
            if (result instanceof ArrayBuffer) {
              try {
                const workbook = XLSX.read(result, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const htmlTable = XLSX.utils.sheet_to_html(worksheet);
                
                fileToView.contentType = 'html';
                fileToView.content = htmlTable;
              } catch (e: any) {
                console.error('Error parsing Excel file:', e);
                fileToView.error = `Error parsing Excel file: ${e.message || 'Unknown error'}`;
              }
            } else {
              fileToView.error = 'Failed to load Excel file: Invalid content';
            }
            break;
            
          case 'png':
          case 'png_thumbnail':
            // For images, use a data URL
            fileToView.contentType = 'base64';
            fileToView.content = result;
            break;
            
          case 'gif':
            // For GIFs, also use a data URL
            fileToView.contentType = 'base64';
            fileToView.content = result;
            break;
            
          case 'html':
            // For HTML files, sanitize and display as HTML
            if (typeof result === 'string') {
              fileToView.contentType = 'html';
              // Create a blob URL for the HTML content to display in iframe
              const htmlBlob = new Blob([result], { type: 'text/html' });
              const htmlUrl = URL.createObjectURL(htmlBlob);
              fileToView.content = this.sanitizer.bypassSecurityTrustResourceUrl(htmlUrl);
              // Also store the original HTML content for raw view
              fileToView.originalContent = result;
            } else {
              fileToView.error = 'Failed to load HTML file: Invalid content';
            }
            break;
            
          default:
            // For unsupported types, provide a download link
            fileToView.contentType = 'blob'; 
            fileToView.content = this.sanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(blob)
            );
            fileToView.needsExternalViewer = true;
            needsExternalViewer = true;
            break;
        }
        
        // Update loading state
        fileToView.isLoading = false;
        
        // If we haven't returned early, emit the prepared file
        this.viewFileInDetails.emit(fileToView);
      };
      
      // Set up error handler
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
        fileToView.error = `Error reading file: ${reader.error?.message || 'Unknown error'}`;
        fileToView.isLoading = false;
        this.viewFileInDetails.emit(fileToView);
      };
      
      // Read the file based on the type
      if (fileType === 'txt' || fileType === 'html') {
        reader.readAsText(blob);
      } else if (fileType === 'docx' || fileType === 'xlsx') {
        reader.readAsArrayBuffer(blob);
      } else if (fileType === 'png' || fileType === 'png_thumbnail' || fileType === 'gif') {
        reader.readAsDataURL(blob);
      } else {
        // For other types, just create a URL
        fileToView.content = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(blob)
        );
        fileToView.isLoading = false;
        this.viewFileInDetails.emit(fileToView);
      }
    } catch (error: any) {
      console.error('Error preparing file for viewing:', error);
      fileToView.error = `Error preparing file: ${error.message || 'Unknown error'}`;
      fileToView.isLoading = false;
      this.viewFileInDetails.emit(fileToView);
    }
  }

  // Close the details pane viewer
  closeViewer(): void {
    // Release any object URLs to avoid memory leaks
    if (this.viewingFile && this.viewingFile.content &&
        typeof this.viewingFile.content === 'object' &&
        'changingThisBreaksApplicationSecurity' in this.viewingFile.content) {
      // Extract the raw URL from the SafeResourceUrl
      const url = this.viewingFile.content['changingThisBreaksApplicationSecurity'];
      if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }

    this.viewerVisible = false;
    this.viewingFile = null;
    this.viewerError = null;
  }

  // Get mime type for a file type
  getFileMimeType(fileType: string): string {
    const mimeTypes: { [key: string]: string } = {
      'txt': 'text/plain',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'pdf': 'application/pdf',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'png': 'image/png',
      'html': 'text/html',
      'png_thumbnail': 'image/png',
      'gif': 'image/gif'
    };
    return mimeTypes[fileType] || 'application/octet-stream';
  }

  // Helper method to check if a specific file is loading
  isFileLoading(fileType: string, action: 'view' | 'download' = 'download', bundleIndex?: number): boolean {
    // If bundleIndex is provided, use the combined key approach
    if (bundleIndex !== undefined) {
      const key = fileType + '-' + bundleIndex;
      if (action === 'view') {
        return !!this.loadingViewFiles[key];
      }
      return !!this.loadingFiles[key];
    } else {
      // Backward compatibility for old code
      if (action === 'view') {
        return !!this.loadingViewFiles[fileType];
      }
      return !!this.loadingFiles[fileType];
    }
  }

  // Download file from a specific bundle
  downloadProcessingFileFromBundle(fileType: string, bundleIndex: number): void {
    console.log('🔽 DEBUG - Download requested:', { fileType, bundleIndex, isLoggedIn: this.isUserLoggedIn });

    // Update selected bundle and file type
    this.selectedBundleIndex = bundleIndex;
    this.selectedFileType = fileType;
    this.bundleId = this.processingFiles[bundleIndex].id;

    if (!this.isUserLoggedIn) {
      console.log('❌ DEBUG - User not logged in, showing login prompt');
      this.showLoginPrompt = true;
      this.cdr.detectChanges(); // Force UI update
      return;
    }

    const selectedBundle = this.processingFiles[bundleIndex];
    if (!selectedBundle || !selectedBundle.files[fileType]) {
      console.log('❌ DEBUG - Bundle or file not found:', { selectedBundle, fileType });
      this.error = `File type ${fileType} not found`;
      return;
    }

    console.log('✅ DEBUG - File found, starting download:', {
      bundleId: this.bundleId,
      fileType,
      fileName: this.getFileNameFromBundle(fileType, selectedBundle)
    });

    // Create combined key for tracking loading state
    const key = fileType + '-' + bundleIndex;
    
    // Set loading state for this specific file in this specific bundle
    this.loadingFiles[key] = true;
    this.error = null;

    // Track file download start in GA4
    const fileName = this.getFileNameFromBundle(fileType, selectedBundle);
    
    console.log('📊 DEBUG - Tracking download start in GA4:', {
      name: fileName,
      type: fileType,
      operation: 'download',
      bundle_id: this.bundleId,
      user_type: this.userType
    });
    
    this.analyticsService.trackFileDownload({
      name: fileName,
      type: fileType,
      size: 0,
      bundle_id: this.bundleId,
      bundle_index: bundleIndex,
      user_type: this.userType
    });

    console.log('🌐 DEBUG - Making API call to download file');

    // Use the bundle-specific endpoint which avoids CORS issues
    this.fileService.downloadFileByType(this.bundleId, fileType).subscribe({
      next: (blob) => {
        console.log('✅ DEBUG - Download successful:', { blobSize: blob.size });
        
        // Reset loading state for this specific file
        this.loadingFiles[key] = false;

        // Get filename from the API response
        const fileName = this.getFileNameFromBundle(fileType, selectedBundle);

        console.log('💾 DEBUG - Saving file:', fileName);
        this.saveFile(blob, fileName);
        
        // Track successful download using the correct download method
        this.analyticsService.trackFileDownload({
          name: fileName,
          type: fileType,
          size: blob.size,
          bundle_id: this.bundleId,
          bundle_index: bundleIndex,
          user_type: this.userType
        });
        
        this.cdr.detectChanges(); // Force UI update
        console.log('🎉 DEBUG - Download completed successfully');
      },
      error: (err) => {
        console.error('❌ DEBUG - Error using bundle-specific endpoint:', err);

        // Track download error using download method with error metadata
        this.analyticsService.trackFileDownload({
          name: fileName,
          type: fileType,
          size: 0,
          bundle_id: this.bundleId,
          bundle_index: bundleIndex,
          user_type: this.userType,
          error_message: err.message || 'Unknown error'
        });

        // If the bundle-specific endpoint fails, try the generic proxy endpoint as fallback
        console.log('🔄 DEBUG - Trying fallback download method');
        this.tryGenericProxyDownloadForBundle(fileType, bundleIndex);
      }
    });
  }

  // New method for bundle-specific proxy download fallback
  private tryGenericProxyDownloadForBundle(fileType: string, bundleIndex: number): void {
    const selectedBundle = this.processingFiles[bundleIndex];
    const fileDetails = selectedBundle?.files?.[fileType];
    const key = fileType + '-' + bundleIndex;

    if (!fileDetails || !fileDetails.link) {
      this.error = 'Failed to download file. File link not found.';
      this.loadingFiles[key] = false; // Reset loading state
      this.cdr.detectChanges();
      return;
    }

    this.fileService.downloadFileFromUrl(fileDetails.link).subscribe({
      next: (blob) => {
        this.loadingFiles[key] = false; // Reset loading state

        // Get the filename
        const fileName = this.getFileNameFromBundle(fileType, selectedBundle);

        this.saveFile(blob, fileName);
        this.cdr.detectChanges(); // Force UI update
      },
      error: (err) => {
        this.error = 'Failed to download file. Please try again later.';
        this.loadingFiles[key] = false; // Reset loading state
        console.error('Error using generic proxy endpoint:', err);
        this.cdr.detectChanges(); // Force UI update
      }
    });
  }

  // Check if a file exists in a specific bundle
  fileExistsInBundle(fileType: string, bundle: FilesResponse): boolean {
    if (!bundle || !bundle.files) {
      return false;
    }

    const file = bundle.files[fileType];
    if (!file) {
      return false;
    }

    // Check if the file has a valid link
    return !!file.link && file.link.trim() !== '';
  }

  // Get file name from a specific bundle
  getFileNameFromBundle(fileType: string, bundle: FilesResponse): string {
    if (!bundle?.files?.[fileType]) {
      return '';
    }

    const fileDetails = bundle.files[fileType];

    // Use the filename field directly from the API response
    if (fileDetails.filename) {
      return fileDetails.filename;
    }

    // Fallback to extracting from URL if filename is not available
    if (fileDetails.link) {
      try {
        const url = new URL(fileDetails.link);
        const pathSegments = url.pathname.split('/');
        return decodeURIComponent(pathSegments[pathSegments.length - 1]) || '';
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
    }

    // If all else fails, return a default name based on file type
    return this.getDefaultFilename(fileType);
  }

  // View file from a specific bundle
  viewFileFromBundle(fileType: string, bundleIndex: number): void {
    console.log('Viewing file:', fileType, 'from bundle:', bundleIndex);
    
    if (bundleIndex >= 0 && bundleIndex < this.processingFiles.length) {
      // Set the selected file type and bundle index for highlighting
      this.selectedFileType = fileType;
      this.selectedBundleIndex = bundleIndex;
      
      // Always open in details pane
      this.viewFile(fileType);
    }
  }

  // Check if in-cell preview should be shown
  shouldShowInCellPreview(): boolean {
    return this.selectedHeight === 'large' && (this.selectedPreview === 'preview' || this.selectedPreview === 'preview-all');
  }

  // Check if previews should be loaded automatically for all files
  shouldAutoLoadAllPreviews(): boolean {
    return this.selectedHeight === 'large' && this.selectedPreview === 'preview-all';
  }

  // Check if previews should be loaded on-demand (click to load)
  shouldLoadOnDemand(): boolean {
    return this.selectedHeight === 'large' && this.selectedPreview === 'preview';
  }

  // Check if thumbnails should be shown
  shouldShowThumbnails(): boolean {
    return this.selectedPreview === 'thumbnail';
  }

  // Check if a thumbnail exists for a specific file in a bundle
  thumbnailExistsInBundle(fileType: string, bundle: FilesResponse): boolean {
    if (!bundle || !bundle.files || !bundle.files[fileType]) {
      return false;
    }

    const file = bundle.files[fileType];
    return !!(file.thumbnail && file.thumbnail.url && file.thumbnail.url.trim() !== '');
  }

  // Get thumbnail URL from a specific bundle
  getThumbnailUrlFromBundle(fileType: string, bundle: FilesResponse): string {
    if (!bundle?.files?.[fileType]?.thumbnail?.url) {
      return '';
    }
    
    // Check if we have a cached blob URL for this thumbnail
    const bundleIndex = this.processingFiles.findIndex(b => b.id === bundle.id);
    const thumbnailKey = `${fileType}-${bundleIndex}`;
    
    if (this.cellThumbnails[thumbnailKey]?.url) {
      return this.cellThumbnails[thumbnailKey].url;
    }
    
    // If not cached, load the thumbnail
    this.loadThumbnailBlob(fileType, bundleIndex, bundle);
    
    // Return empty string until thumbnail is loaded
    return '';
  }

  // Get thumbnail filename from a specific bundle
  getThumbnailFilenameFromBundle(fileType: string, bundle: FilesResponse): string {
    if (!bundle?.files?.[fileType]?.thumbnail?.filename) {
      return '';
    }
    return bundle.files[fileType].thumbnail.filename;
  }

  // Get thumbnail created date from a specific bundle
  getThumbnailCreatedDateFromBundle(fileType: string, bundle: FilesResponse): string {
    if (!bundle?.files?.[fileType]?.thumbnail?.created_at) {
      return '';
    }
    const dateStr = bundle.files[fileType].thumbnail.created_at;
    
    // Handle different date formats
    if (!dateStr || dateStr.trim() === '') {
      return '';
    }
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr; // Return original string if date parsing fails
      }
      return date.toISOString();
    } catch (error) {
      return dateStr; // Return original string if error occurs
    }
  }

  // Load thumbnail through backend API and create blob URL
  private loadThumbnailBlob(fileType: string, bundleIndex: number, bundle: FilesResponse): void {
    const thumbnailKey = `${fileType}-${bundleIndex}`;
    
    // Initialize loading state
    this.cellThumbnails[thumbnailKey] = {
      url: null,
      isLoading: true,
      error: null
    };

    // Get the thumbnail URL from the bundle
    const thumbnailUrl = bundle.files[fileType]?.thumbnail?.url;
    if (!thumbnailUrl) {
      this.cellThumbnails[thumbnailKey] = {
        url: null,
        isLoading: false,
        error: 'No thumbnail URL available'
      };
      this.cdr.detectChanges();
      return;
    }

    // Use the existing proxy endpoint instead of a dedicated thumbnail endpoint
    this.fileService.downloadFileFromUrl(thumbnailUrl).subscribe({
      next: (blob) => {
        // Create blob URL
        const blobUrl = URL.createObjectURL(blob);
        this.cellThumbnails[thumbnailKey] = {
          url: blobUrl,
          isLoading: false,
          error: null
        };
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load thumbnail:', error);
        this.cellThumbnails[thumbnailKey] = {
          url: null,
          isLoading: false,
          error: 'Failed to load thumbnail'
        };
        this.cdr.detectChanges();
      }
    });
  }

  // Check if thumbnail is loading
  isThumbnailLoading(fileType: string, bundleIndex: number): boolean {
    const thumbnailKey = `${fileType}-${bundleIndex}`;
    return this.cellThumbnails[thumbnailKey]?.isLoading || false;
  }

  // Get thumbnail error
  getThumbnailError(fileType: string, bundleIndex: number): string | null {
    const thumbnailKey = `${fileType}-${bundleIndex}`;
    return this.cellThumbnails[thumbnailKey]?.error || null;
  }

  // Load preview content into the cell
  loadInCellPreview(fileType: string, bundleIndex: number): void {
    const previewKey = `${fileType}-${bundleIndex}`;
    
    // Initialize preview state
    this.cellPreviews[previewKey] = {
      content: null,
      isLoading: true,
      error: null
    };

    // Use existing file loading logic but store in cellPreviews instead of emitting
    if (bundleIndex >= 0 && bundleIndex < this.processingFiles.length) {
      const bundle = this.processingFiles[bundleIndex];
      
      if (this.fileExistsInBundle(fileType, bundle)) {
        const fileName = this.getFileNameFromBundle(fileType, bundle);
        
        // Use the same download method that works for regular downloads
        this.loadFileContentForPreview(fileType, bundleIndex, previewKey);
      } else {
        this.cellPreviews[previewKey] = {
          content: null,
          isLoading: false,
          error: 'File not available'
        };
      }
    }
  }

  // Get preview content for a specific cell
  getCellPreviewContent(fileType: string, bundleIndex: number): string | SafeResourceUrl | null {
    const previewKey = `${fileType}-${bundleIndex}`;
    return this.cellPreviews[previewKey]?.content || null;
  }

  // Check if cell preview is loading
  isCellPreviewLoading(fileType: string, bundleIndex: number): boolean {
    const previewKey = `${fileType}-${bundleIndex}`;
    return this.cellPreviews[previewKey]?.isLoading || false;
  }

  // Check if cell preview has error
  getCellPreviewError(fileType: string, bundleIndex: number): string | null {
    const previewKey = `${fileType}-${bundleIndex}`;
    return this.cellPreviews[previewKey]?.error || null;
  }

  // Load file content specifically for preview (modified version of existing download logic)
  private async loadFileContentForPreview(fileType: string, bundleIndex: number, previewKey: string): Promise<void> {
    try {
      const bundle = this.processingFiles[bundleIndex];
      const fileName = this.getFileNameFromBundle(fileType, bundle);
      
      // Use the same download method that works for regular downloads
      this.fileService.downloadFileByType(bundle.id, fileType).subscribe({
        next: async (blob) => {
          await this.processFileForPreview(blob, fileType, fileName, previewKey);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Preview download failed:', error);
          this.cellPreviews[previewKey] = {
            content: null,
            isLoading: false,
            error: `Failed to load preview: ${error.message || 'Unknown error'}`
          };
          this.cdr.detectChanges();
        }
      });
      
    } catch (error) {
      console.error(`Error in loadFileContentForPreview:`, error);
      this.cellPreviews[previewKey] = {
        content: null,
        isLoading: false,
        error: 'Failed to load preview'
      };
      this.cdr.detectChanges();
    }
  }

  // Process file content for preview display
  private async processFileForPreview(blob: Blob, fileType: string, fileName: string, previewKey: string): Promise<void> {
    try {
      let content: string | SafeResourceUrl | null = null;

      switch (fileType) {
        case 'txt':
          const textContent = await blob.text();
          content = textContent;
          break;
        
        case 'png':
        case 'png_thumbnail':
        case 'gif':
          const imageUrl = URL.createObjectURL(blob);
          content = this.sanitizer.bypassSecurityTrustResourceUrl(imageUrl);
          break;
        
        case 'html':
          const htmlContent = await blob.text();
          content = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
          break;
        
        case 'pdf':
          const pdfUrl = URL.createObjectURL(blob);
          content = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
          break;
        
        case 'docx':
          try {
            const arrayBuffer = await blob.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            content = this.sanitizer.bypassSecurityTrustHtml(result.value);
          } catch (error) {
            console.error('Error converting DOCX:', error);
            content = 'Preview not available for this DOCX file';
          }
          break;
        
        case 'xlsx':
          try {
            const arrayBuffer = await blob.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const htmlContent = XLSX.utils.sheet_to_html(worksheet);
            content = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
          } catch (error) {
            console.error('Error converting XLSX:', error);
            content = 'Preview not available for this Excel file';
          }
          break;
        
        default:
          content = 'Preview not available for this file type';
      }

      this.cellPreviews[previewKey] = {
        content,
        isLoading: false,
        error: null
      };

    } catch (error) {
      console.error('Error processing file for preview:', error);
      this.cellPreviews[previewKey] = {
        content: null,
        isLoading: false,
        error: 'Failed to process file for preview'
      };
    }
  }

  // Get headers for authenticated requests (reuse existing method)
  private getHeaders(): { [key: string]: string } {
    const googleUser = localStorage.getItem('googleUser');
    const headers: { [key: string]: string } = {};
    
    if (googleUser) {
      try {
        const userInfo = JSON.parse(googleUser);
        if (userInfo.email) {
          headers['X-User-Email'] = userInfo.email;
        }
      } catch (error) {
        console.error('Error parsing stored user info:', error);
      }
    }
    
    return headers;
  }

  triggerFileUploadForBundle(fileType: string, bundleIndex: number): void {
    // Update selected bundle and file type
    this.selectedBundleIndex = bundleIndex;
    this.selectedFileType = fileType;
    this.bundleId = this.processingFiles[bundleIndex].id;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = this.getFileAccept(fileType);
    fileInput.click();

    fileInput.addEventListener('change', (event) => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const file = files[0];

        // Validate file
        const validation = this.validateFile(file, fileType);
        if (!validation.valid) {
          // Use combined key to track error state
          this.uploadError[fileType + '-' + bundleIndex] = validation.message;
          this.cdr.detectChanges();
          return;
        }

        // Track file upload start in GA4
        this.analyticsService.trackFileUpload({
          name: file.name,
          type: fileType,
          size: file.size,
          bundle_id: this.bundleId,
          bundle_index: bundleIndex,
          user_type: this.userType
        });

        // Use combined key for state tracking
        this.uploading[fileType + '-' + bundleIndex] = true;
        this.uploadError[fileType + '-' + bundleIndex] = null;
        this.uploadSuccess[fileType + '-' + bundleIndex] = false;

        const formData = new FormData();
        formData.append('file', file);

        this.fileService.uploadFileToBundle(this.bundleId, fileType, formData).subscribe({
          next: (response) => {
            this.uploading[fileType + '-' + bundleIndex] = false;
            this.uploadSuccess[fileType + '-' + bundleIndex] = true;
            
            // Track successful upload in GA4
            this.analyticsService.trackFileUpload({
              name: file.name,
              type: fileType,
              size: file.size,
              bundle_id: this.bundleId,
              bundle_index: bundleIndex,
              user_type: this.userType
            });
            
            // Reload the files after successful upload
            this.loadFileBundle();
            this.fileOpEvents.emit('refresh'); // Emit refresh event
            // Reset success indicator after 3 seconds
            setTimeout(() => {
              this.uploadSuccess[fileType + '-' + bundleIndex] = false;
              this.cdr.detectChanges();
            }, 3000);
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.uploading[fileType + '-' + bundleIndex] = false;
            this.uploadError[fileType + '-' + bundleIndex] = 'Failed to upload file. Please try again.';
            
            // Track upload error in GA4
            this.analyticsService.trackFileUpload({
              name: file.name,
              type: fileType,
              size: file.size,
              bundle_id: this.bundleId,
              bundle_index: bundleIndex,
              user_type: this.userType,
              error_message: err.message || 'Unknown error'
            });
            
            console.error('Error uploading file:', err);
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  // Replace file in a specific bundle
  replaceFileInSpecificBundle(fileType: string, bundleIndex: number): void {
    // Update selected bundle and file type
    this.selectedBundleIndex = bundleIndex;
    this.selectedFileType = fileType;
    this.bundleId = this.processingFiles[bundleIndex].id;

    if (!this.isUserLoggedIn) {
      this.showLoginPrompt = true;
      this.cdr.detectChanges(); // Force UI update
      return;
    }

    if (!this.canPerformAction('replace')) {
      this.error = 'You do not have permission to replace files.';
      return;
    }

    // Check if the file exists
    if (!this.fileExistsInBundle(fileType, this.processingFiles[bundleIndex])) {
      this.error = `No ${fileType} file exists to replace. Use Upload instead.`;
      return;
    }

    // Create a temporary file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = this.getFileAccept(fileType);
    fileInput.click();

    fileInput.addEventListener('change', (event) => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const file = files[0];

        // Validate file
        const validation = this.validateFile(file, fileType);
        if (!validation.valid) {
          // Use combined key for tracking error state
          this.uploadError[fileType + '-' + bundleIndex] = validation.message;
          this.cdr.detectChanges();
          return;
        }

        // Track file replace start in GA4
        this.analyticsService.trackFileReplace({
          name: file.name,
          type: fileType,
          size: file.size,
          bundle_id: this.bundleId,
          bundle_index: bundleIndex,
          user_type: this.userType
        });

        // Use combined key for operation states
        this.uploading[fileType + '-' + bundleIndex] = true;
        this.uploadError[fileType + '-' + bundleIndex] = null;
        this.uploadSuccess[fileType + '-' + bundleIndex] = false;

        const formData = new FormData();
        formData.append('file', file);

        this.fileService.replaceFileInBundle(this.bundleId, fileType, formData).subscribe({
          next: (response) => {
            this.uploading[fileType + '-' + bundleIndex] = false;
            this.uploadSuccess[fileType + '-' + bundleIndex] = true;
            
            // Track successful replace in GA4
            this.analyticsService.trackFileReplace({
              name: file.name,
              type: fileType,
              size: file.size,
              bundle_id: this.bundleId,
              bundle_index: bundleIndex,
              user_type: this.userType
            });
            
            // Reload the files after successful replacement
            this.loadFileBundle();
            this.fileOpEvents.emit('refresh'); // Emit refresh event
            // Reset success indicator after 3 seconds
            setTimeout(() => {
              this.uploadSuccess[fileType + '-' + bundleIndex] = false;
              this.cdr.detectChanges();
            }, 3000);
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.uploading[fileType + '-' + bundleIndex] = false;
            this.uploadError[fileType + '-' + bundleIndex] = 'Failed to replace file. Please try again.';
            
            // Track replace error in GA4
            this.analyticsService.trackFileReplace({
              name: file.name,
              type: fileType,
              size: file.size,
              bundle_id: this.bundleId,
              bundle_index: bundleIndex,
              user_type: this.userType,
              error_message: err.message || 'Unknown error'
            });
            
            console.error('Error replacing file:', err);
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  // Modify any upload/delete/replace methods to check adminMode before showing controls
  isActionAllowed(): boolean {
    // For internal users, always allow actions regardless of admin mode
    return this.userType === 'internal';
  }

  // Method to handle checkbox change
  onCheckboxChange(bundle: FilesResponse): void {
    // If the same bundle is clicked again, uncheck it
    if (this.checkedBundleId === bundle.id) {
      this.checkedBundleId = null;
    } else {
      this.checkedBundleId = bundle.id;
      // Select the bundle index for highlighting
      const index = this.processingFiles.findIndex(b => b.id === bundle.id);
      if (index !== -1) {
        this.selectBundle(index);
      }
    }
  }

  // Edit the selected bundle
  editSelectedBundle(): void {
    if (this.checkedBundleId) {
      console.log('Editing bundle with ID:', this.checkedBundleId);
      // Implement your edit functionality here
      // For example, you could open a dialog with the bundle details for editing
    }
  }

  // Delete the selected bundle
  deleteSelectedBundle(): void {
    if (this.checkedBundleId) {
      console.log('Deleting bundle with ID:', this.checkedBundleId);
      // Implement your delete functionality here
      // For example, show a confirmation dialog and delete if confirmed
      
      // After successful deletion:
      // this.checkedBundleId = null;
      // this.loadFileBundle(); // Reload the list
    }
  }

  // Column resizing methods
  private initResizeListeners(): void {
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);

    // Create resize indicator element
    this.resizeIndicator = document.createElement('div');
    this.resizeIndicator.className = 'resize-indicator';
    this.resizeIndicator.style.display = 'none';
    document.body.appendChild(this.resizeIndicator);
  }

  private ensureTableElement(): HTMLElement | null {
    if (!this.tableElement) {
      this.tableElement = document.querySelector('.mat-file-table')
        || document.querySelector('table[mat-table]');
      if (this.tableElement) {
        // Apply initial column widths once table is found
        Object.keys(this.columnWidths).forEach(column => {
          this.applyColumnWidth(column, this.columnWidths[column]);
        });
      }
    }
    return this.tableElement;
  }

  private removeResizeListeners(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    
    // Remove resize indicator
    if (this.resizeIndicator && this.resizeIndicator.parentNode) {
      this.resizeIndicator.parentNode.removeChild(this.resizeIndicator);
    }
  }

  startResize(event: MouseEvent, column: string): void {
    this.ensureTableElement();
    this.isResizing = true;
    this.currentResizeColumn = column;
    this.startX = event.pageX;
    
    const headerCell = (event.target as HTMLElement).closest('th');
    if (headerCell) {
      this.startWidth = headerCell.offsetWidth;
      
      // Show and position the resize indicator
      if (this.resizeIndicator) {
        const tableRect = this.tableElement?.getBoundingClientRect();
        const headerRect = headerCell.getBoundingClientRect();
        if (tableRect) {
          this.resizeIndicator.style.top = `${tableRect.top}px`;
          this.resizeIndicator.style.left = `${headerRect.right}px`;
          this.resizeIndicator.style.height = `${tableRect.height}px`;
          this.resizeIndicator.style.display = 'block';
        }
      }
      
      // Add active class to the resize handle
      // Find the resize handle element either from the event target or from the header cell
      let resizeHandle: HTMLElement | null = null;
      if ((event.target as HTMLElement).classList.contains('resize-handle')) {
        resizeHandle = event.target as HTMLElement;
      } else {
        resizeHandle = headerCell.querySelector('.resize-handle');
      }
      
      if (resizeHandle) {
        resizeHandle.classList.add('active');
      }
    }
    
    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isResizing || !this.currentResizeColumn) return;
    
    const diff = event.pageX - this.startX;
    const newWidth = Math.max(100, this.startWidth + diff); // Minimum width: 100px
    
    const table = this.ensureTableElement();
    // Update indicator position
    if (this.resizeIndicator && table) {
      // Angular Material tables use mat-column-[columnName] OR mat-mdc-column-[columnName]
      const headerCell = table.querySelector(`th.mat-column-${this.currentResizeColumn}, th.mat-mdc-column-${this.currentResizeColumn}`);
      if (headerCell) {
        const headerRect = headerCell.getBoundingClientRect();
        this.resizeIndicator.style.left = `${headerRect.left + newWidth}px`;
        
        // Live updating - Apply the width change immediately during dragging
        this.applyColumnWidth(this.currentResizeColumn, newWidth);
        
        // Store the current width (will be finalized on mouseup)
        this.columnWidths[this.currentResizeColumn] = newWidth;
      }
    }
  };

  private onMouseUp = (event: MouseEvent): void => {
    if (!this.isResizing || !this.currentResizeColumn) return;
    
    // Get the final width after resize
    const headerCell = this.tableElement?.querySelector(`th.mat-column-${this.currentResizeColumn}, th.mat-mdc-column-${this.currentResizeColumn}`);
    if (headerCell) {
      // Get current mouse position relative to the start position 
      const diff = event.pageX - this.startX; // Use the mouse event parameter
      const newWidth = Math.max(100, this.startWidth + diff);
      
      console.log(`Resizing ${this.currentResizeColumn} to ${newWidth}px`);
      
      // Apply the new width one final time
      this.applyColumnWidth(this.currentResizeColumn, newWidth);
      
      // Store the new width
      this.columnWidths[this.currentResizeColumn] = newWidth;
      
      // Remove active class from resize handle
      const resizeHandle = headerCell.querySelector('.resize-handle');
      if (resizeHandle) {
        resizeHandle.classList.remove('active');
      }
    }
    
    // Hide resize indicator
    if (this.resizeIndicator) {
      this.resizeIndicator.style.display = 'none';
    }
    
    this.isResizing = false;
    this.currentResizeColumn = null;
  };

  private applyColumnWidth(column: string, width: number): void {
    if (!this.tableElement) return;
    
    // Support both standard and MDC-based Material column selectors
    const headerCells = this.tableElement.querySelectorAll(`th.mat-column-${column}, th.mat-mdc-column-${column}`);
    const dataCells = this.tableElement.querySelectorAll(`td.mat-column-${column}, td.mat-mdc-column-${column}`);
    
    // Apply width to header cells
    headerCells.forEach((cell: Element) => {
      (cell as HTMLElement).style.width = `${width}px`;
      (cell as HTMLElement).style.minWidth = `${width}px`;
      (cell as HTMLElement).style.maxWidth = `${width}px`;
    });
    
    // Apply width to data cells
    dataCells.forEach((cell: Element) => {
      (cell as HTMLElement).style.width = `${width}px`;
      (cell as HTMLElement).style.minWidth = `${width}px`;
      (cell as HTMLElement).style.maxWidth = `${width}px`;
    });
  }

  // Check if a specific bundle is currently selected
  isSelectedBundle(index: number): boolean {
    return this.selectedBundleIndex === index;
  }

  // YouTube-related methods
  hasYouTubeVideo(bundle: FilesResponse): boolean {
    return !!(bundle.youtube && bundle.youtube.has_video && bundle.youtube.video_url);
  }

  viewYouTubeVideo(bundle: FilesResponse): void {
    if (!bundle.youtube || !bundle.youtube.has_video) {
      console.warn('No YouTube video available for this bundle');
      return;
    }

    // Set selected file type and bundle for highlighting
    this.selectedFileType = 'youtube';
    this.selectedBundleIndex = this.processingFiles.findIndex(b => b.id === bundle.id);

    // Analytics tracking for YouTube video view
    if (this.analyticsService) {
      // Use sendGtagEvent for YouTube tracking since trackCustomEvent is private
      this.analyticsService['sendGtagEvent']('youtube_video_view', {
        bundle_id: bundle.id,
        video_id: bundle.youtube.video_id,
        video_title: bundle.youtube.title,
        session_id: this.analyticsService['sessionId']
      });
    }

    // Emit YouTube video for in-app viewing in details pane
    this.viewFileInDetails.emit({
      fileType: 'youtube',
      content: this.sanitizer.bypassSecurityTrustResourceUrl(bundle.youtube.embed_url),
      fileName: bundle.youtube.title || 'YouTube Video',
      mimeType: 'video/youtube',
      contentType: 'youtube',
      isLoading: false,
      originalUrl: bundle.youtube.watch_url,
      youtubeData: bundle.youtube // Add YouTube-specific data
    });
  }

  openYouTubeVideo(videoUrl?: string): void {
    if (!videoUrl) {
      console.warn('No YouTube video URL provided');
      return;
    }
    window.open(videoUrl, '_blank');
  }

  addYouTubeVideo(bundleIndex: number): void {
    if (this.userType !== 'internal') {
      console.warn('Unauthorized action: Only internal users can add YouTube videos');
      return;
    }

    const videoUrl = prompt('Enter YouTube video URL:');
    if (!videoUrl) return;

    // Validate YouTube URL format
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(videoUrl)) {
      alert('Please enter a valid YouTube URL');
      return;
    }

    const bundle = this.processingFiles[bundleIndex];
    if (!bundle) {
      console.error('Bundle not found for index:', bundleIndex);
      return;
    }

    // Call API to add YouTube video
    this.callYouTubeAPI('add', bundle.id, videoUrl);
  }

  updateYouTubeVideo(bundleIndex: number): void {
    if (this.userType !== 'internal') {
      console.warn('Unauthorized action: Only internal users can update YouTube videos');
      return;
    }

    const bundle = this.processingFiles[bundleIndex];
    if (!bundle || !bundle.youtube) {
      console.error('Bundle or YouTube data not found');
      return;
    }

    const newVideoUrl = prompt('Enter new YouTube video URL:', bundle.youtube.video_url);
    if (!newVideoUrl) return;

    // Validate YouTube URL format
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(newVideoUrl)) {
      alert('Please enter a valid YouTube URL');
      return;
    }

    // Call API to update YouTube video
    this.callYouTubeAPI('update', bundle.id, newVideoUrl);
  }

  removeYouTubeVideo(bundleIndex: number): void {
    if (this.userType !== 'internal') {
      console.warn('Unauthorized action: Only internal users can remove YouTube videos');
      return;
    }

    const bundle = this.processingFiles[bundleIndex];
    if (!bundle) {
      console.error('Bundle not found for index:', bundleIndex);
      return;
    }

    if (!confirm('Are you sure you want to remove this YouTube video?')) {
      return;
    }

    // Call API to remove YouTube video
    this.callYouTubeAPI('remove', bundle.id);
  }

  private callYouTubeAPI(action: 'add' | 'update' | 'remove', bundleId: number, videoUrl?: string): void {
    // Show loading state
    this.loading = true;

    let apiCall: any;
    const baseUrl = `${this.fileService['baseUrl']}/api/file-bundles/${bundleId}/youtube`;

    switch (action) {
      case 'add':
        apiCall = this.http.put(baseUrl, { video_url: videoUrl });
        break;
      case 'update':
        apiCall = this.http.put(baseUrl, { video_url: videoUrl });
        break;
      case 'remove':
        apiCall = this.http.delete(baseUrl);
        break;
    }

    apiCall.subscribe({
      next: (response: any) => {
        console.log(`YouTube ${action} successful:`, response);
        
        // Analytics tracking
        if (this.analyticsService) {
          this.analyticsService['sendGtagEvent'](`youtube_video_${action}`, {
            bundle_id: bundleId,
            video_url: videoUrl,
            session_id: this.analyticsService['sessionId']
          });
        }

        // Reload the bundles to get updated data
        this.loadFileBundle();
      },
      error: (error: any) => {
        console.error(`YouTube ${action} failed:`, error);
        this.error = `Failed to ${action} YouTube video. Please try again.`;
        this.loading = false;
      }
    });
  }

  onYouTubeThumbnailError(event: any): void {
    // Set a YouTube-style placeholder using data URL
    event.target.src = 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="120" height="90" viewBox="0 0 120 90">
        <rect width="120" height="90" fill="#f0f0f0"/>
        <rect x="40" y="30" width="40" height="30" rx="20" fill="#ff0000"/>
        <polygon points="55,37 55,53 67,45" fill="white"/>
        <text x="60" y="75" text-anchor="middle" font-family="Arial" font-size="10" fill="#666">YouTube</text>
      </svg>
    `);
  }

  // Handle thumbnail image errors
  onThumbnailImageError(event: any): void {
    console.error('Thumbnail image failed to load:', event.target.src);
    // Instead of hiding, show a placeholder
    const img = event.target as HTMLImageElement;
    img.src = 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
        <rect width="60" height="60" fill="#f0f0f0" stroke="#ddd" stroke-width="1"/>
        <text x="30" y="30" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="20" fill="#999">📄</text>
        <text x="30" y="45" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="8" fill="#666">No Image</text>
      </svg>
    `);
    img.style.display = 'block';
  }

  // Handle thumbnail image successful load
  onThumbnailImageLoad(event: any): void {
    console.log('Thumbnail loaded successfully:', event.target.src);
  }

  // Auto-load thumbnails when thumbnail mode is enabled
  private loadThumbnailsForVisibleBundles(): void {
    if (!this.shouldShowThumbnails()) {
      return;
    }

    this.processingFiles.forEach((bundle, bundleIndex) => {
      this.fileTypes.forEach(fileType => {
        if (this.thumbnailExistsInBundle(fileType, bundle)) {
          const thumbnailKey = `${fileType}-${bundleIndex}`;
          // Only load if not already loaded or loading
          if (!this.cellThumbnails[thumbnailKey]) {
            this.loadThumbnailBlob(fileType, bundleIndex, bundle);
          }
        }
      });
    });
  }
}
